// Checkbox list of Facebook ad accounts. Local-only state — the parent card
// owns the "Save Selection" action so we don't fire a PATCH on every toggle.

import type { FacebookAdAccount } from '@/types'

interface Props {
  accounts: FacebookAdAccount[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

const STATUS_LABELS: Record<number, string> = {
  1: 'Active',
  2: 'Disabled',
  3: 'Unsettled',
  7: 'Pending review',
  8: 'In grace period',
  9: 'Pending closure',
  100: 'Pending settlement',
  101: 'Any active',
  102: 'Any closed',
}

export function AdAccountSelector({ accounts, selectedIds, onChange, disabled }: Props) {
  if (accounts.length === 0) {
    return (
      <p className="text-sm text-jarvis-muted py-2">
        No ad accounts found. Make sure your Facebook user has access to at least one ad account in Business Manager.
      </p>
    )
  }

  const sel = new Set(selectedIds)
  const toggle = (id: string) => {
    const next = new Set(sel)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(Array.from(next))
  }

  return (
    <div className="space-y-1.5">
      {accounts.map((a) => {
        const checked = sel.has(a.id)
        return (
          <label
            key={a.id}
            className={`flex items-center gap-3 px-2.5 py-2 rounded-md border text-sm cursor-pointer transition-colors ${
              checked
                ? 'border-jarvis-blue/40 bg-jarvis-blue/5'
                : 'border-jarvis-border hover:border-jarvis-blue/30'
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(a.id)}
              className="accent-jarvis-blue"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.name || a.id}</div>
              <div className="text-xs text-jarvis-muted">
                {a.id} · {a.currency || '—'} · {STATUS_LABELS[a.account_status] ?? `status ${a.account_status}`}
              </div>
            </div>
          </label>
        )
      })}
    </div>
  )
}
